/**
 * Быстрые действия: разрешение «сторона × режим цены» в готовую команду.
 *
 * Модуль чистый — ни сети, ни React, ни времени изнутри, — потому что здесь
 * решается, ПО КАКОЙ ЦЕНЕ уйдут настоящие деньги, и это правило обязано
 * проверяться тестом, а не разглядыванием компонента.
 *
 * Быстрое действие — это (сторона × режим цены). Размер и исход — режимы
 * панели, а не часть кнопки: 2 стороны × 2 режима × 4 размера дали бы
 * шестнадцать кнопок, среди которых оператор искал бы нужную дольше, чем набрал
 * бы форму.
 *
 * ВАЖНО. Набор режимов цены не может расширяться в одиночку: трейдер
 * пред-подписывает ровно «встать в лучшую» и «перешагнуть спред» (плюс
 * PRESIGN_LEVELS шагов вокруг них). Третий режим — это сначала правка
 * trader/presigner, и только потом кнопка здесь; иначе новая кнопка окажется
 * самой медленной на экране.
 */

import type { Market, OrderType, PlaceOrderRequest, Side } from "../api/types";
import { best, type AssetBook } from "../store/book";
import { free, known, takerFee } from "./fees";
import { SCALE, add, cmp, format, mul, parse, sub, type Micro } from "./money";
import { floorToSizeStep, sharesForUsd } from "./size";

/** Режим цены быстрого действия. */
export type PriceMode = "join" | "cross";

export interface QuickAction {
  side: Side;
  mode: PriceMode;
}

/** Четыре действия панели в порядке отрисовки: строка покупки над строкой продажи. */
export const QUICK_ACTIONS: ReadonlyArray<QuickAction> = [
  { side: "BUY", mode: "join" },
  { side: "BUY", mode: "cross" },
  { side: "SELL", mode: "join" },
  { side: "SELL", mode: "cross" },
];

export function actionKey(a: QuickAction): string {
  return `${a.side}:${a.mode}`;
}

/**
 * Подпись действия для эха и подсказок.
 *
 * Названо ролью в сделке — Maker / Taker, — а не механикой («встать в стакан»,
 * «перейти спред»). Механику надо переводить в голове каждый раз, а роль скальпер
 * и так держит в уме: она решает, платит он спред или получает его, и она же
 * определяет тип ордера (GTC против FAK, см. orderTypeFor). «L1» — уровень, на
 * который встаёт мейкер: первый, лучший в стакане; других мы не выставляем, и
 * именно поэтому уровень назван явно.
 *
 * Исход подставляется ВЕРСАЛЬЮ и ровно так же, как на кнопке: эхо описывает уже
 * отправленную команду, и оператор сверяет его с тем, что нажал. Разойдись эти
 * две надписи в написании — сверка превратилась бы в перевод.
 */
export function actionLabel(a: QuickAction, outcome?: string): string {
  const verb = a.side === "BUY" ? "Купить" : "Продать";
  const role = a.mode === "join" ? "Maker L1" : "Taker";
  const name = outcome?.trim();
  return name ? `${verb} ${name.toUpperCase()}: ${role}` : `${verb}: ${role}`;
}

/**
 * Тип ордера для режима цены.
 *
 * FAK, а НЕ FOK, для «перейти спред», и это решение, а не вкус.
 *
 * FOK — всё или ничего против уровня, размер которого агрегирован и всё время
 * меняется. Если пресет 100, а на лучшем аске стоит 40, FOK вернёт ничего и не
 * объяснит почему: биржа скажет «не исполнен». Скальпер, ничего не узнавший,
 * жмёт ещё раз по картинке, которая уже на круг устарела.
 *
 * FAK берёт сорок, остаток снимает, и size_matched в списке ордеров честно
 * рассказывает, что в стакане действительно было. Цена при этом лимитная,
 * поэтому единственный минус — неполный лот, а он строго дешевле выброшенного
 * обращения к бирже на вехе, критерий приёмки которой — задержка этого
 * обращения.
 *
 * Отдельный довод в пользу FAK именно на M5: пред-подписанный билет может
 * отстать от рынка на шаг, и FOK превращает это отставание в полную потерю, а
 * FAK — в частичное исполнение.
 */
export function orderTypeFor(mode: PriceMode): OrderType {
  return mode === "join" ? "GTC" : "FAK";
}

export type Resolved =
  | {
      ok: true;
      req: PlaceOrderRequest;
      price: string;
      /** Сумма в долларах. При покупке её нажал оператор, при продаже — следствие. */
      usd: string;
      /** Размер в share — то, что уйдёт на биржу. */
      size: string;
      tickSize: string;
      /**
       * Комиссия этого действия в долларах, строкой; null — «не знаем».
       *
       * Считается ЗДЕСЬ, а не в компоненте, потому что здесь уже известна роль:
       * `join` встаёт в стакан мейкером и не платит ничего, `cross` переходит
       * спред тейкером и платит. Ровно эта разница и есть цена нетерпения, ради
       * показа которой веха M7 и делалась.
       *
       * null означает «расписание рынка незнакомо» и обязан гаситься прочерком.
       * Ноль — законное значение: у мейкера комиссии нет.
       */
      fee: string | null;
      /**
       * Деньги, которые РЕАЛЬНО уйдут или придут: у покупки `usd + fee`, у
       * продажи `usd − fee`.
       *
       * Отдельно от usd, потому что usd — это то, что оператор нажал, а это —
       * то, что он получит. На «$25» тейкерской покупки по 0.74 разница
       * составляет 45 центов, и молчать о ней нельзя. Равно usd, когда комиссия
       * неизвестна или нулевая.
       */
      usdNet: string;
      /**
       * Чем был задан размер. Решает порядок величин в подписи кнопки: при
       * покупке оператор нажимает доллары и share — следствие, при продаже
       * наоборот.
       */
      basis: "usd" | "shares";
    }
  | { ok: false; reason: string };

export interface QuickContext {
  market: Market;
  assetId: string;
  book: AssetBook | undefined;
  /**
   * Сумма В ДОЛЛАРАХ — путь ПОКУПКИ. Приходит из пресетов оператора или из
   * поля ручного ввода.
   *
   * Ровно одно из usd/shares. Двусмысленность закрывается ГРОМКО в рантайме, а
   * не размеченным объединением: путь usd основной и единственный у покупки, у
   * клика по лесенке и у всех прежних вызовов, и дискриминант заставил бы
   * каждый из них писать служебный тег ради исключительного случая.
   */
  usd?: string;
  /**
   * Размер В SHARE — путь ПРОДАЖИ доли позиции.
   *
   * Отдельным полем, а не переводом доли в доллары: доллары пришлось бы делить
   * обратно на цену уровня, и округление вниз на обратном ходе продало бы не
   * ту долю, которую нажал оператор. «Продать всё» обязано продавать всё.
   */
  shares?: string;
}

/**
 * Причина, по которой панель не может собрать команду из состояния стакана.
 *
 * Возвращается ОТДЕЛЬНО от разрешения действия, потому что относится ко всем
 * четырём кнопкам сразу, и повторять её на каждой значило бы засыпать оператора
 * одним и тем же текстом.
 *
 * Формулировки повторяют BookLadder: два описания одного состояния, сказанные
 * по-разному, заставляют оператора гадать, разные ли это состояния. У
 * неподтверждённого стакана к бейджу лесенки дописано последствие — панель, в
 * отличие от лесенки, этим состоянием ГАСИТ кнопки, и молчать о причине нельзя.
 */
export function bookProblem(book: AssetBook | undefined): string | null {
  if (!book) return "стакан ещё не запрошен";
  switch (book.status) {
    case "waiting":
      return "загрузка стакана…";
    case "absent":
      return "нет данных: трейдер не публикует стакан по этому токену";
    case "error":
      return book.error ?? "стакан недоступен";
  }
  // Неподтверждённый стакан — ПОСЛЕ статуса: у waiting/absent/error причина
  // конкретнее, и она же объясняет, почему подтверждения нет.
  //
  // Проверяется здесь, а не в компоненте, ровно потому, что отсюда причину
  // видят оба пути отправки: и подпись кнопки, и хоткей, который её минует и
  // зовёт resolveQuick напрямую.
  //
  // Формулировка называет ПОСЛЕДСТВИЕ, а не только состояние: «не подтверждён»
  // само по себе выглядит косметикой, а отказывает по этому флагу риск-гейт
  // трейдера (правило RuleBookStale), и оператору важно, что кнопка погасла не
  // из осторожности интерфейса, а потому что ордер всё равно не уйдёт.
  if (book.snapshotStale) return "стакан не подтверждён — трейдер откажет по нему";
  return null;
}

/**
 * Ориентировочная цена рынка — для подсказок ДО нажатия.
 *
 * Нужна ровно там, где надо показать оператору «$5 на этом рынке — это меньше
 * минимального размера», не зная ещё, какую из четырёх кнопок он нажмёт. Берётся
 * лучший ask, а при его отсутствии — лучший bid: ask даёт БОЛЬШУЮ цену, то есть
 * МЕНЬШИЙ размер, и подсказка ошибается в безопасную сторону — предупредит там,
 * где сделка ещё прошла бы, а не промолчит там, где не пройдёт.
 *
 * Для самой отправки не используется: там цена берётся из разрешённого уровня.
 */
export function referencePrice(book: AssetBook | undefined): string | null {
  if (!book) return null;
  const ask = best(book.asks);
  if (ask !== null) return format(ask.p);
  const bid = best(book.bids);
  return bid === null ? null : format(bid.p);
}

/**
 * Цена действия по стакану — без размера.
 *
 * Отдельно от resolveQuick, потому что от размера она не зависит: мейкер встаёт
 * ровно на уровень, тейкер берёт вершину с запасом (takerLimit), а не проход по
 * лесенке. Нужна там, где цена одна на несколько нажатий: в шапке таблицы
 * быстрых действий цены стоят один раз на все строки аккаунтов.
 */
export function quotePrice(
  action: QuickAction,
  market: Market,
  book: AssetBook | undefined,
): { ok: true; price: string; priceMicro: Micro; tickSize: string } | { ok: false; reason: string } {
  const problem = bookProblem(book);
  if (problem !== null) return { ok: false, reason: problem };

  const b = book!;
  // Шаг берётся из СТАКАНА, а не из справочника: tick_size_change доходит до
  // стакана раньше, и по устаревшему шагу панель отвергала бы верную цену и
  // пропускала неверную. То же правило, что в OrderForm.
  const tickSize = b.tickSize || market.tick_size;

  const bid = best(b.bids);
  const ask = best(b.asks);

  // BUY встаёт по лучшему bid и переходит спред по лучшему ask; SELL — зеркально.
  const wantAsk = (action.side === "BUY") === (action.mode === "cross");
  const level = wantAsk ? ask : bid;
  if (level === null) {
    return {
      ok: false,
      reason: wantAsk
        ? "нет предложений на продажу — цену взять неоткуда"
        : "нет заявок на покупку — цену взять неоткуда",
    };
  }

  // format(level.p), а не level.price: на проводе приходит «0.5» там, где сетка
  // оператора показывает «0.50», и канонизация через микро-единицы делает
  // отправляемую строку такой же, какую дал бы snapPrice формы. Это важно
  // ровно потому, что трейдер сравнивает ноги лесенки по ЗНАЧЕНИЮ.
  //
  // Тейкер берёт вершину С ЗАПАСОМ В НЕСКОЛЬКО ШАГОВ, мейкер — ровно уровень:
  // см. takerLimit и TAKER_SLACK_TICKS.
  const priceMicro = action.mode === "cross" ? takerLimit(action.side, level.p, tickSize) : level.p;
  if (priceMicro === null) {
    // Запас уже сжат до одного шага (takerLimit), и даже он не поместился.
    return { ok: false, reason: `цена ${format(level.p)} с запасом на шаг выходит за границы` };
  }
  const price = format(priceMicro);
  return { ok: true, price, priceMicro, tickSize };
}

/**
 * Разрешает быстрое действие в команду.
 *
 * Кнопка выключается не потому, что мы запрещаем, а потому, что цену взять
 * неоткуда. Запрет — дело трейдера; отсутствие входных данных — не запрет.
 * Именно поэтому взведённый аварийный стоп сюда НЕ входит: при нём команда
 * прекрасно собирается, уходит и получает авторитетный отказ, который оператор
 * обязан увидеть.
 */
export function resolveQuick(action: QuickAction, ctx: QuickContext): Resolved {
  const quote = quotePrice(action, ctx.market, ctx.book);
  if (!quote.ok) return quote;
  const { price, priceMicro, tickSize } = quote;

  const sized = resolveSize(ctx, price);
  if (!sized.ok) return sized;
  const { size, usd, basis } = sized;

  // Минимум рынка проверяется ЗДЕСЬ, а не по списку пресетов: он задан в share,
  // а пресет — в долларах, и годность суммы зависит от цены. $5 по 0.52 даёт
  // 9.61 share, а по 0.99 — уже 5.05: одна и та же кнопка на одном рынке
  // проходит, на другом нет. Для пути shares проверка та же и по той же
  // причине: доля позиции тоже бывает мельче минимума.
  const min = parse(ctx.market.min_order_size);
  const got = parse(size);
  if (min !== null && min > 0 && got !== null && cmp(got, min) < 0) {
    return {
      ok: false,
      reason:
        basis === "usd"
          ? `$${usd} по цене ${price} даёт ${size} share — ` +
            `меньше минимального размера рынка (${format(min)})`
          : `${size} share — меньше минимального размера рынка (${format(min)})`,
    };
  }

  const req: PlaceOrderRequest = {
    condition_id: ctx.market.condition_id,
    asset_id: ctx.assetId,
    side: action.side,
    price,
    size,
    order_type: orderTypeFor(action.mode),
    // expires_at ОТСУТСТВУЕТ, а не undefined: exactOptionalPropertyTypes, и
    // пред-подпись не готовит GTD вовсе (срок задаётся в момент нажатия).
    //
    // post_only — у мейкерской кнопки и только у неё. Кнопка называет себя
    // «Maker L1» и обещает, что комиссии нет (resolveFee возвращает по этой
    // ветке ноль), но САМА ПО СЕБЕ цена лучшего уровня роли мейкера не
    // обещает: пока команда идёт через шину и трейдера, встречная сторона
    // успевает опуститься до неё, и ордер исполняется тейкером — платя ту
    // комиссию, которой на кнопке написано «нет». Флаг превращает обещание в
    // условие: биржа либо ставит ордер в стакан, либо отвергает целиком.
    //
    // Тейкерской кнопки это не касается вовсе: она перекрывает стакан
    // намеренно, и post_only отверг бы КАЖДЫЙ её ордер. Пред-подпись флаг тоже
    // не трогает: он лежит в обёртке тела POST /order, а не в подписи, и
    // пред-подписанный билет годится с ним и без него.
    ...(action.mode === "join" ? { post_only: true } : {}),
  };
  const { fee, usdNet } = resolveFee(action, ctx.market, priceMicro, got, usd);
  return { ok: true, req, price, usd, size, tickSize, fee, usdNet, basis };
}

/**
 * Комиссия действия и деньги, которые реально уйдут или придут.
 *
 * Роль читается из режима цены и ниоткуда больше: `join` встаёт в стакан
 * мейкером и платит ноль, `cross` переходит спред тейкером и платит. Это
 * измеренный факт, а не допущение — у наших мейкерских филлов на цепочке
 * комиссия ноль при `makerBaseFee: 1000` в расписании рынка.
 *
 * Но факт этот измерен на РАСПИСАНИИ, а не вообще: ноль у мейкера следует из
 * `taker_only`, и на расписании без него мейкер платит тоже. Поэтому мейкерская
 * ветка спрашивает расписание ровно так же, как тейкерская: «бесплатно» — это
 * утверждение о деньгах, и брать его с потолка нельзя.
 *
 * Возврат мейкеру (`rebateRate`) в цену кнопки НЕ входит: в сделке он не
 * приходит вовсе, а раздаётся раз в сутки помимо сделок, и приписать его
 * конкретному нажатию нельзя.
 */
function resolveFee(
  action: QuickAction,
  market: Market,
  priceMicro: Micro,
  sizeMicro: Micro | null,
  usd: string,
): { fee: string | null; usdNet: string } {
  if (action.mode === "join") {
    // Мейкер не платит — при условии, что расписание это подтверждает: `known`
    // требует taker_only, `free` означает рынок вовсе без комиссии. Ноль здесь —
    // знание, а не его отсутствие, и получен он тем же способом, что число рядом.
    if (!known(market.fees) && !free(market.fees)) {
      return { fee: null, usdNet: usd };
    }
    return { fee: "0", usdNet: usd };
  }
  const feeMicro = sizeMicro === null ? null : takerFee(market.fees, priceMicro, sizeMicro);
  const money = parse(usd);
  if (feeMicro === null || money === null) {
    // Расписание незнакомо — прочерк вместо числа. Показать здесь ноль значило бы
    // объявить переход спреда бесплатным, а он не бесплатен.
    return { fee: null, usdNet: usd };
  }
  // Покупка платит СВЕРХ нотионала, продажа — из выручки. Измерено на цепочке:
  // «$5» тейкерской покупки стоили $5.28.
  const net = action.side === "BUY" ? add(money, feeMicro) : sub(money, feeMicro);
  return { fee: format(feeMicro), usdNet: net === null ? usd : format(net) };
}

/**
 * Не нашлось ли встречной заявки под тейкерский ордер.
 *
 * Это НЕ отказ и не поломка: FAK по определению гасится, если исполнить нечего,
 * и на пятиминутном рынке уровень успевает уйти за время полёта команды. Ордер
 * не создан, деньги не потрачены, позиция не изменилась — единственное верное
 * действие оператора — посмотреть на новую цену и решить заново.
 *
 * Красный цвет в этой панели зарезервирован за тем, что требует разбирательства:
 * отказ риск-гейта, неверные суммы, 401. Покрасив им ушедшую цену, мы приучаем
 * не читать красное — и настоящий отказ проходит мимо глаз.
 *
 * Сверяется по подстроке ответа биржи, потому что кода у неё нет: 400 с текстом
 * «no orders found to match with FAK order» — это всё, что она сообщает.
 */
export function isNoMatch(error: string): boolean {
  return error.includes("no orders found to match");
}

/**
 * Лимитная цена ТЕЙКЕРА: вершина встречной стороны плюс шаг в сторону сделки.
 *
 * # Зачем запас
 *
 * Taker — это «купить по рынку», а не «купить ровно по L1». Лимит ровно по
 * вершине делает ордер заложником ОДНОГО шага: пока команда летит до биржи,
 * уровень успевают забрать, FAK не находит встречной заявки и гасится, ничего
 * не исполнив. Оператор при этом видел на кнопке цену, которая на момент клика
 * была настоящей, — и получает отказ, выглядящий как поломка.
 *
 * Случай, из которого выросло правило: 2026-08-09, 21:16:40,
 * btc-updown-5m-1786310100. Покупка Down по 0.2 отвергнута с «no orders found to
 * match with FAK order». История цены биржи по этому окну — 0.525 → 0.605 →
 * 0.235 → 0.145 → 0.675 → 0.995 — показывает, что стакан просто уехал за время
 * полёта команды.
 *
 * # Почему TAKER_SLACK_TICKS шагов, а не один
 *
 * Прежде запас был ровно в один шаг, и довода было два. Первый — «запас нужен на
 * время полёта команды» — верен и сейчас. Второй — «два шага выпадут из кэша
 * пред-подписи, ордер будет подписываться на лету и уйдёт медленнее» —
 * опровергнут замером прода 2026-08-22: стадия подписи, В КОТОРУЮ ВХОДИТ и
 * промах кэша, стоит `place_sign_us = 4 / 139` (микросекунды), тогда как само
 * обращение к бирже — `clob_order_latency_ms = 112 / 331`. Подпись на горячем
 * пути дешевле сетевого хода на три порядка, и торговать шириной запаса ради
 * неё не за что. PRESIGN_LEVELS всё равно поднят до того же числа — чтобы
 * лесенка кэша покрывала запас целиком, — но это оптимизация, а не условие.
 *
 * Почему именно два. Тем же замером взято расхождение НАШЕЙ вершины стакана с
 * биржевой: 33 % сверок расходятся, p90 = 2 цента, максимум 9. Запас в один шаг
 * не покрывает p90 — и это ровно то, что оператор видел как «клик не прошёл»:
 * доля убитых FAK за 2026-08-22 росла с 18 % до 46 % за четыре часа, причина у
 * всех одна — «no orders found to match with FAK order». Запас в два шага
 * закрывает p90. Третий шаг закрыл бы последние проценты ценой ещё одного цента
 * проскальзывания на каждой сделке — размен, который стоит делать, только если
 * замер повторится с худшими числами.
 *
 * # Почему это не переплата
 *
 * Лимитная цена тейкера — ПОТОЛОК, а не цена сделки: исполняется он по
 * встречным уровням стакана, и улучшение биржа отдаёт тейкеру. Купить дороже,
 * чем стоит лучший уровень, этот запас не заставляет — он лишь разрешает сделку,
 * если лучший уровень успел сместиться.
 *
 * # Границы
 *
 * Границы (0, 1) — те же, что у ордера вообще: цена вне их не ордер. Запас
 * поэтому СЖИМАЕТСЯ до того числа шагов, которое ещё помещается, а не отвергает
 * действие целиком: у кромки (ask 0.98 при шаге 0.01) полный запас вышел бы за
 * единицу, и отказ здесь означал бы, что панель разучилась делать то, что делала
 * с запасом в один шаг. Именно в зоне 0.95–0.99 живёт передача хода, ради
 * которой панель и существует. null остаётся ровно за прежним случаем — когда не
 * помещается и ОДИН шаг.
 */
export const TAKER_SLACK_TICKS = 2;

function takerLimit(side: Side, level: Micro, tickSize: string): Micro | null {
  const tick = parse(tickSize);
  if (tick === null || tick <= 0) return null;
  // От полного запаса к одному шагу: берём первый, который помещается в (0, 1).
  for (let steps = TAKER_SLACK_TICKS; steps >= 1; steps--) {
    const limit = side === "BUY" ? level + steps * tick : level - steps * tick;
    if (limit > 0 && limit < SCALE) return limit;
  }
  return null;
}

/**
 * Размер ордера: из суммы (покупка) или из доли позиции (продажа).
 *
 * Отказы здесь ГРОМКИЕ и в обе стороны. Заданы оба источника — это дефект
 * вызывающего кода, и подобрать «правильный» молча значило бы отправить ордер
 * размера, которого никто не просил; не задан ни один — то же самое с нулём.
 */
function resolveSize(
  ctx: QuickContext,
  price: string,
): { ok: true; size: string; usd: string; basis: "usd" | "shares" } | { ok: false; reason: string } {
  const hasUsd = ctx.usd !== undefined && ctx.usd !== "";
  const hasShares = ctx.shares !== undefined && ctx.shares !== "";

  if (hasUsd && hasShares) {
    return { ok: false, reason: "внутренняя ошибка: размер задан и в долларах, и в share" };
  }

  if (hasShares) {
    // Размер уже в share и уже прижат к шагу вызывающим (lib/position.ts), но
    // проверяется здесь ещё раз: это последний рубеж перед PlaceOrderRequest, а
    // размер с шестью знаками биржа отвергает ордером целиком.
    const raw = parse(ctx.shares!);
    if (raw === null) return { ok: false, reason: `размер ${ctx.shares} не разобран` };
    const size = floorToSizeStep(raw);
    if (size <= 0) {
      return { ok: false, reason: "доля позиции меньше 0.01 share — продать её нельзя" };
    }
    // Доллары считаются из цены и размера: оператор нажал share, а сколько это
    // денег, он всё равно обязан увидеть до нажатия.
    const notional = mul(size, parse(price) ?? 0);
    return {
      ok: true,
      size: format(size),
      usd: notional === null ? "—" : format(notional),
      basis: "shares",
    };
  }

  if (!hasUsd) return { ok: false, reason: "размер не задан" };

  // Размер выводится из СУММЫ и РАЗРЕШЁННОЙ ЦЕНЫ, а не задаётся заранее: цена
  // берётся из стакана в момент нажатия, и $10 по 0.52 — это другой размер,
  // чем $10 по 0.53. Правило то же, каким лесенка пред-подписи считала размер
  // билета (signing.SharesForUSD), иначе билета по ключу не нашлось бы.
  const size = sharesForUsd(ctx.usd!, price);
  if (size === null) {
    return {
      ok: false,
      reason: `суммы $${ctx.usd} не хватает даже на 0.01 share по цене ${price}`,
    };
  }
  return { ok: true, size, usd: ctx.usd!, basis: "usd" };
}
